import { useState } from 'react';
import { WorkspaceIcon, type WorkspaceIconName } from '../icons';
import { connectionLogoUrl } from '../connection-logo';
import type { ConnectionRowState } from './rows';

/**
 * A connection's logo tile with its state on the corner: a check when connected, a mark when it
 * needs attention, a ring when turned off. Rows, catalogue tiles and the panel all draw this one.
 */
export function ConnectionMark({
  name,
  src,
  logoId,
  glyph,
  state = null,
  size = 'md',
}: {
  name: string;
  /** The catalogue's logo: undefined while it is still being read, null when there is none. */
  src?: string | null;
  /** A vendored mark under /connector-logos, tried when there is no catalogue logo. */
  logoId?: string;
  /** Drawn instead of a logo (your own server, other assistants). */
  glyph?: WorkspaceIconName;
  state?: ConnectionRowState | null;
  size?: 'md' | 'lg' | 'xl';
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const image = src ?? (src === undefined || !logoId ? null : connectionLogoUrl(logoId));
  return (
    <span className="ws-conn-mark" data-size={size} data-glyph={glyph ? 'true' : undefined}>
      {glyph ? (
        <WorkspaceIcon name={glyph} size={size === 'md' ? 19 : 24} />
      ) : image && failed !== image ? (
        <img src={image} alt="" loading="lazy" onError={() => setFailed(image)} />
      ) : src === undefined && logoId ? null : (
        <span className="ws-conn-mark-letter" aria-hidden="true">
          {name.trim().slice(0, 1).toLocaleUpperCase()}
        </span>
      )}
      {state && (
        <span className="ws-conn-mark-badge" data-state={state} aria-hidden="true">
          {state !== 'off' && (
            <svg viewBox="0 0 24 24" width="9" height="9">
              <path d={state === 'connected' ? 'm5 12.5 4.2 4.2L19 7' : 'M12 6.5v7m0 4.2h.01'} />
            </svg>
          )}
        </span>
      )}
    </span>
  );
}
